/**
 * 全 board の合法手を WASM (= `getEngine(game_id).legal`) で計算するフック。
 *
 * - snapshot.boards が更新されるたびに走る (Renju 黒禁手も legal に含まれる)
 * - wasm 未 ready なら no-op (Play.tsx が ensureWasm 中の場合に備える)
 * - 着手側の Side による絞り込みは盤コンポーネント側の責務 (= ここでは全合法手を返す)
 *
 * **キャッシュ**: WS で snapshot が再 broadcast されると `boards[i].state` の参照が
 * 毎回新規になる (DO が JSON で再シリアライズ)。 しかし内容が同じなら WASM 再計算は無意味で、
 * 終盤 BG の `legal` は片側で 20〜30 手 + DFS 内部で apply を回す重い計算。 boards 数 ×
 * 毎 snapshot で全 board 分を回すと、 他盤の進行ごとに全 board の WASM コールが走り UI が固まる
 * (= 「3 番盤の終盤で操作きかない」 主要因)。 内容ハッシュ一致した盤はキャッシュした legal 配列
 * 参照をそのまま再利用し、 ref を維持することで下流の useMemo / React.memo も効くようにする。
 */

import { useEffect, useRef, useState } from 'react';
import type { BoardEntry } from '../lib/match-snapshot';
import { getEngine } from '../wasm';

interface CacheEntry {
  key: string;
  legal: unknown[];
}

export function useLegalMoves(
  boards: BoardEntry[],
  wasmReady: boolean,
): Record<number, unknown[]> {
  const [legalByBoard, setLegalByBoard] = useState<Record<number, unknown[]>>({});
  const cacheRef = useRef<Map<number, CacheEntry>>(new Map());
  useEffect(() => {
    if (!wasmReady || boards.length === 0) return;
    const cache = cacheRef.current;
    const next: Record<number, unknown[]> = {};
    const seen = new Set<number>();
    for (const b of boards) {
      seen.add(b.board);
      const key = JSON.stringify(b.state);
      const cached = cache.get(b.board);
      if (cached && cached.key === key) {
        next[b.board] = cached.legal; // 参照そのまま再利用
        continue;
      }
      try {
        const legal = getEngine(b.game_id).legal(b.state) as unknown[];
        cache.set(b.board, { key, legal });
        next[b.board] = legal;
      } catch (e) {
        console.error('[legal] failed for board', b.board, b.game_id, e);
        next[b.board] = [];
        cache.delete(b.board);
      }
    }
    for (const id of Array.from(cache.keys())) {
      if (!seen.has(id)) cache.delete(id);
    }
    setLegalByBoard((cur) => {
      // 全 board が ref 一致なら setState 省略 (= 再 render 抑止)
      const curKeys = Object.keys(cur);
      if (curKeys.length === Object.keys(next).length) {
        let same = true;
        for (const k of curKeys) {
          const id = Number(k);
          if (cur[id] !== next[id]) {
            same = false;
            break;
          }
        }
        if (same) return cur;
      }
      return next;
    });
  }, [wasmReady, boards]);
  return legalByBoard;
}
